import { useEffect, useState } from "react";
import { SYNC_PHASE_LABELS, type SyncPhase } from "@legal-500/redis-db-manager-types";
import { dbManagerService, type KeyspaceProgressDetails } from "../../../api/dbManager.service.js";
import styles from "./DbManager.module.css";

const POLL_INTERVAL_MS = 3000;

type SlotProgressBarProps = {
  keyspace: string;
  phase?: SyncPhase | null;
};

function phaseLabel(phase: SyncPhase | null | undefined): string {
  if (!phase) return "Starting…";
  return SYNC_PHASE_LABELS[phase] ?? phase;
}

export function SlotProgressBar({ keyspace, phase }: SlotProgressBarProps) {
  const [details, setDetails] = useState<KeyspaceProgressDetails | null>(null);
  const [pendingPhase, setPendingPhase] = useState<SyncPhase | null>(null);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    async function poll() {
      try {
        const response = await dbManagerService.getKeyspaceProgress(keyspace);
        if (cancelled) return;
        setDetails(response.details);
        setPendingPhase(response.pendingPhase);
      } catch {
        if (cancelled) return;
        setDetails(null);
        setPendingPhase(null);
      } finally {
        if (!cancelled) {
          timer = setTimeout(poll, POLL_INTERVAL_MS);
        }
      }
    }

    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [keyspace]);

  // The sync subprocess's phase wins once it starts reporting one; before
  // that, fall back to the platform-api's k8s-derived pre-sync phase.
  const effectivePhase = phase ?? pendingPhase;
  const isSyncingPages =
    effectivePhase === "syncing-pages" && details !== null && details.totalPages > 0;
  const total = isSyncingPages ? details!.totalPages : 0;
  const percent = isSyncingPages
    ? Math.min(100, Math.round((details!.processedCount / total) * 100))
    : 0;

  return (
    <div
      className={styles.slotProgressOverlay}
      data-testid={`slot-progress-${keyspace}`}
      role="progressbar"
      aria-valuenow={isSyncingPages ? percent : undefined}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={`Slot ${keyspace} sync progress`}
    >
      <div className={styles.slotProgressOverlayTrack}>
        {isSyncingPages ? (
          <div
            className={`${styles.slotProgressOverlayFill} ${styles.slotProgressOverlayFillActive}`}
            style={{ width: `${percent}%` }}
          />
        ) : (
          <div className={styles.slotProgressOverlayIndeterminate} />
        )}
      </div>
      <div className={styles.slotProgressOverlayCaption}>
        {isSyncingPages ? (
          <>
            <span className={styles.slotProgressOverlayPercent}>{percent}%</span>
            <span className={styles.slotProgressOverlayStats}>
              {details!.processedCount.toLocaleString()} / {total.toLocaleString()}
              {details!.failedCount > 0 && (
                <span className={styles.slotProgressOverlayFailed}>
                  {" · "}
                  {details!.failedCount.toLocaleString()} failed
                </span>
              )}
              {details!.pagesPerSecond > 0 && (
                <>
                  {" · "}
                  {details!.pagesPerSecond.toFixed(1)}/s
                </>
              )}
            </span>
          </>
        ) : (
          <span className={styles.slotProgressOverlayPhase}>{phaseLabel(effectivePhase)}</span>
        )}
      </div>
    </div>
  );
}
